import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { downloadBackup } from "../../lib/downloadBackup";
import { useProgress } from "../../store/progress";
import { saveBeforeReload } from "../../store/reload";

function PageRecovery() {
  const { data, protectedBackup } = useProgress();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const active = useRef(false);
  const pending = useRef(false);
  useEffect(() => {
    active.current = true;
    heading.current?.focus();
    return () => {
      active.current = false;
    };
  }, []);
  async function retry() {
    if (pending.current) return;
    pending.current = true;
    setSaving(true);
    setMessage("");
    const safe = await saveBeforeReload();
    if (!active.current) return;
    if (safe) {
      // React.lazy and the browser cache rejected imports. A persisted reload
      // resets both; resetting only the boundary would repeat the same failure.
      window.location.reload();
    } else {
      pending.current = false;
      setSaving(false);
      setMessage(
        "已阻止刷新：记录仅在内存、保存失败或仍有未保存改动。请先导出当前学习备份，当前草稿没有被清除。",
      );
    }
  }
  function rescue(backup: unknown, prefix: string) {
    try {
      downloadBackup(backup, prefix);
      setMessage(
        "备份已生成，请确认浏览器已完成下载并妥善保存。导出不会清除当前记录。",
      );
    } catch {
      setMessage(
        "备份生成失败，请不要关闭或刷新页面。可以切换其他课程重试，当前内存记录仍保留。",
      );
    }
  }
  return (
    <section
      className="empty-state page-recovery"
      aria-labelledby="page-recovery-title"
    >
      <h1 id="page-recovery-title" ref={heading} tabIndex={-1}>
        学习面板暂时无法打开
      </h1>
      <p>
        页面资源加载失败或界面发生异常。课程目录仍可使用，学习记录没有被清除。
      </p>
      <p>
        检查连接后可保存并刷新重试；若仍然失败，请先导出备份，避免反复刷新。
      </p>
      <div className="recovery-actions">
        <button
          className="primary"
          disabled={saving}
          onClick={() => void retry()}
        >
          {saving ? "正在保存并重新加载…" : "保存并重新加载页面"}
        </button>
        <button onClick={() => rescue(data, "fls-learning")}>
          导出当前学习备份
        </button>
        {protectedBackup !== undefined && (
          <button
            onClick={() => rescue(protectedBackup, "fls-protected-original")}
          >
            导出受保护的原始记录
          </button>
        )}
      </div>
      {message && <p role="status">{message}</p>}
      <Link to="/learn/react-state">返回 React 课程 →</Link>
    </section>
  );
}

/** Reset only a failed boundary on navigation; preserve healthy editor/undo state. */
export default class PageErrorBoundary extends Component<
  { children: ReactNode; routeKey: string },
  { failed: boolean; routeKey: string }
> {
  state = { failed: false, routeKey: this.props.routeKey };
  static getDerivedStateFromProps(
    props: { routeKey: string },
    state: { routeKey: string },
  ) {
    return props.routeKey !== state.routeKey
      ? { routeKey: props.routeKey, failed: false }
      : null;
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <PageRecovery /> : this.props.children;
  }
}
